import { useEffect, useMemo, useState } from 'react';

export const useContentListSelection = ({
  dataSource = [],
  normalizeContentType,
  listQueryModeRef,
  loadSearchData,
  loadData,
}) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedTypeById, setSelectedTypeById] = useState({});

  const selectedRowKeySet = useMemo(() => new Set(selectedRowKeys || []), [selectedRowKeys]);

  useEffect(() => {
    if (!dataSource || dataSource.length === 0) return;
    if (!selectedRowKeys || selectedRowKeys.length === 0) return;

    setSelectedTypeById((prev) => {
      let changed = false;
      const next = { ...(prev || {}) };
      for (const row of dataSource) {
        const id = row?.content_id;
        if (!id) continue;
        if (!selectedRowKeySet.has(id)) continue;
        const t = normalizeContentType(row?.content_type);
        if (t && next[id] !== t) {
          next[id] = t;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [dataSource, normalizeContentType, selectedRowKeySet, selectedRowKeys]);

  useEffect(() => {
    if ((selectedRowKeys || []).length === 0) {
      setSelectedTypeById({});
    }
  }, [selectedRowKeys]);

  const currentPageContentIds = (dataSource || [])
    .map((item) => item.content_id)
    .filter(Boolean);

  const isAllCurrentPageSelected = currentPageContentIds.length > 0
    && currentPageContentIds.every((id) => selectedRowKeys.includes(id));

  const toggleSelectAllCurrentPage = () => {
    if (currentPageContentIds.length === 0) return;

    setSelectedRowKeys((prevKeys) => {
      const otherPageSelected = (prevKeys || []).filter((key) => !currentPageContentIds.includes(key));
      const prevAllCurrent = currentPageContentIds.every((id) => (prevKeys || []).includes(id));
      const nextKeys = prevAllCurrent
        ? otherPageSelected
        : Array.from(new Set([...otherPageSelected, ...currentPageContentIds]));

      setSelectedTypeById((prev) => {
        const next = {};
        for (const k of nextKeys) {
          if (prev && prev[k]) next[k] = prev[k];
        }
        if (!prevAllCurrent) {
          for (const row of dataSource || []) {
            const id = row?.content_id;
            if (!id) continue;
            if (!nextKeys.includes(id)) continue;
            const t = normalizeContentType(row?.content_type);
            if (t) next[id] = t;
          }
        }
        return next;
      });

      return nextKeys;
    });
  };

  const rowSelection = {
    selectedRowKeys: selectedRowKeys.filter((key) =>
      dataSource.some((item) => item.content_id === key)
      || !dataSource.some((item) => item.content_id === key)
    ),
    onChange: (keys, rows) => {
      const currentPageIds = dataSource.map((item) => item.content_id);
      const otherPageSelected = selectedRowKeys.filter((key) => !currentPageIds.includes(key));
      const uniqueKeys = Array.from(new Set([...otherPageSelected, ...keys]));
      setSelectedRowKeys(uniqueKeys);
      setSelectedTypeById((prev) => {
        const next = {};
        for (const k of uniqueKeys) {
          if (prev && prev[k]) next[k] = prev[k];
        }
        for (const row of rows || []) {
          const id = row?.content_id;
          if (!id) continue;
          const t = normalizeContentType(row?.content_type);
          if (t) next[id] = t;
        }
        return next;
      });
    },
  };

  const clearSelection = () => setSelectedRowKeys([]);

  const onTablePageChange = (page, pageSize) => {
    if (listQueryModeRef.current === 'search') {
      loadSearchData(page, pageSize);
      return;
    }
    loadData({ page, page_size: pageSize });
  };

  const onTablePageSizeChange = (_, size) => {
    if (listQueryModeRef.current === 'search') {
      loadSearchData(1, size);
      return;
    }
    loadData({ page: 1, page_size: size });
  };

  return {
    selectedRowKeys,
    setSelectedRowKeys,
    selectedTypeById,
    isAllCurrentPageSelected,
    toggleSelectAllCurrentPage,
    rowSelection,
    clearSelection,
    onTablePageChange,
    onTablePageSizeChange,
  };
};
